import React from 'react';
import { toast } from 'react-toastify';
import { handleError } from '../../common/utils/util.errors';
import { deepCopy } from '../../common/utils/common.util';
import keys from 'common/react-query/data-keys';
import { useQueryClient } from 'react-query';
import { CCReport } from './construction-cost-report.type';
import { getCCReport, updateCCReport, updateCCReportComps } from './construction-cost-report.api';
import CCReportRenderer from './CCReportRenderer';
import { useNavigate } from 'react-router-dom';

interface CCReportWrapperProps {
  initialReport: CCReport;
}

export const CCReportWrapper: React.FC<CCReportWrapperProps> = ({ initialReport }) => {
  const [report, setReport] = React.useState<CCReport>(deepCopy(initialReport));
  const [isReportDirty, setIsReportDirty] = React.useState(false);
  const [isReportLoading, setIsReportLoading] = React.useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  React.useEffect(() => {
    setReport(deepCopy(initialReport));
    setIsReportDirty(false);
  }, [initialReport]);

  const updateReport = (updatedReport: CCReport) => {
    setReport(updatedReport);
    setIsReportDirty(true);
  };

  const resetReport = () => {
    setReport(deepCopy(initialReport));
    setIsReportDirty(false);
  };

  const saveReport = async () => {
    try {
      setIsReportLoading(true);
      await updateCCReport(report.id, report);
      await updateCCReportComps(report.id, report.comparableProjects ?? []);
      toast.success('All Changes Saved');
      setIsReportDirty(false);

      const updatedReport = await getCCReport(report.id);
      setReport(updatedReport);
      queryClient.invalidateQueries([keys.ccReport, report.id]);
    } catch (error) {
      handleError(error);
    } finally {
      setIsReportLoading(false);
    }
  };

  const refetchReport = async () => {
    try {
      setIsReportLoading(true);
      const updatedReport = await getCCReport(report.id);
      setReport(updatedReport);
      setIsReportDirty(false);
      queryClient.invalidateQueries([keys.ccReport, report.id]);
    } catch (error) {
      handleError(error);
    } finally {
      setIsReportLoading(false);
    }
  };

  return (
    <CCReportRenderer
      report={report}
      updateReport={updateReport}
      isReportDirty={isReportDirty}
      isReportLoading={isReportLoading}
      saveReport={saveReport}
      undoChanges={resetReport}
      refetchReport={refetchReport}
    />
  );
};
